import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBell } from '@fortawesome/free-solid-svg-icons'
import { notificationsApiService, NotificationType, type Notification } from '../services/notificationsApiService'
import { sanitizeHtml } from '../utils/sanitizeHtml'
import { colors, fontSize, radius, shadow, spacing } from '../styles/theme'

const POLL_MS = 60_000
const fmt = (s: string) => new Date(s).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })

/**
 * Header bell with an unread badge + a dropdown of recent notifications, plus the interrupting modal
 * for unread warning/announcement notifications (shown on next interaction). Polls quietly.
 */
export function NotificationCenter() {
  const [items, setItems] = useState<Notification[]>([])
  const [unread, setUnread] = useState(0)
  const [open, setOpen] = useState(false)
  const [modalQueue, setModalQueue] = useState<Notification[]>([])
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const load = useCallback(async () => {
    try {
      const [list, modal] = await Promise.all([
        notificationsApiService.list(),
        notificationsApiService.unreadModal(),
      ])
      setItems(list.items ?? [])
      setUnread(list.unread ?? 0)
      setModalQueue(modal ?? [])
    } catch { /* quiet — polling */ }
  }, [])

  useEffect(() => {
    load()
    const t = setInterval(load, POLL_MS)
    return () => clearInterval(t)
  }, [load])

  useEffect(() => {
    function onDoc(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  async function markRead(n: Notification) {
    if (n.readAt) return
    try { await notificationsApiService.markRead(n.notificationId) } catch { /* ignore */ }
    setItems(prev => prev.map(i => i.notificationId === n.notificationId ? { ...i, readAt: new Date().toISOString() } : i))
    setUnread(u => Math.max(0, u - 1))
  }

  async function markAll() {
    try { await notificationsApiService.markAllRead() } catch { /* ignore */ }
    setItems(prev => prev.map(i => ({ ...i, readAt: i.readAt ?? new Date().toISOString() })))
    setUnread(0)
  }

  async function dismissModal(n: Notification) {
    try { await notificationsApiService.markRead(n.notificationId) } catch { /* ignore */ }
    setModalQueue(q => q.filter(x => x.notificationId !== n.notificationId))
    setItems(prev => prev.map(i => i.notificationId === n.notificationId ? { ...i, readAt: new Date().toISOString() } : i))
    setUnread(u => Math.max(0, u - 1))
  }

  // System notifications deep-link into the app with a relative href (e.g. /app/esign?request=…). Route those
  // client-side instead of reloading the page; absolute links (admin-authored) keep their default behaviour.
  function followInAppLink(e: React.MouseEvent, after: () => void) {
    const a = (e.target as HTMLElement).closest('a')
    const href = a?.getAttribute('href')
    if (!href || !href.startsWith('/') || href.startsWith('//') || a?.target === '_blank') return
    e.preventDefault()
    after()
    navigate(href)
  }

  const current = modalQueue[0]

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        aria-label="Notifications"
        onClick={() => setOpen(o => !o)}
        style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary, padding: 6, display: 'flex', alignItems: 'center' }}
      >
        <FontAwesomeIcon icon={faBell} style={{ width: 18, height: 18 }} />
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: -2, right: -2, minWidth: 16, height: 16, padding: '0 4px',
            background: colors.error, color: '#fff', borderRadius: 8, fontSize: 10, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
          }}>{unread > 99 ? '99+' : unread}</span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 36, width: 340, maxHeight: 420, overflowY: 'auto',
          background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md,
          boxShadow: shadow.lg, zIndex: 1000,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: `${spacing.sm} ${spacing.md}`, borderBottom: `1px solid ${colors.border}` }}>
            <span style={{ fontWeight: 600, color: colors.text, fontSize: fontSize.sm }}>Notifications</span>
            {unread > 0 && <button onClick={markAll} style={{ background: 'none', border: 'none', color: colors.primary, cursor: 'pointer', fontSize: fontSize.xs }}>Mark all read</button>}
          </div>
          {items.length === 0 && <div style={{ padding: spacing.lg, color: colors.textSecondary, fontSize: fontSize.sm, textAlign: 'center' }}>No notifications</div>}
          {items.map(n => (
            <div key={n.notificationId} onClick={e => { markRead(n); followInAppLink(e, () => setOpen(false)) }}
              style={{ padding: `${spacing.sm} ${spacing.md}`, borderBottom: `1px solid ${colors.border}`, cursor: n.readAt ? 'default' : 'pointer', background: n.readAt ? 'transparent' : colors.primaryLight }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {n.type === NotificationType.WARNING && <span style={{ width: 8, height: 8, borderRadius: 4, background: colors.error }} />}
                <span style={{ fontWeight: n.readAt ? 400 : 600, color: colors.text, fontSize: fontSize.sm }}>{n.title}</span>
              </div>
              {n.body && <div style={{ color: colors.textSecondary, fontSize: fontSize.xs, marginTop: 2, overflow: 'hidden' }}
                className="notif-body" dangerouslySetInnerHTML={{ __html: sanitizeHtml(n.body) }} />}
              <div style={{ color: colors.textSecondary, fontSize: 10, marginTop: 2 }}>{fmt(n.createdAt)}</div>
            </div>
          ))}
        </div>
      )}

      {current && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }}>
          <div style={{
            background: colors.surface, borderRadius: radius.md, width: 440, maxWidth: '100%', overflow: 'hidden',
            border: `2px solid ${current.type === NotificationType.WARNING ? colors.error : colors.border}`,
          }}>
            <div style={{
              padding: `${spacing.sm} ${spacing.lg}`,
              background: current.type === NotificationType.WARNING ? colors.errorLight : colors.primaryLight,
              color: current.type === NotificationType.WARNING ? colors.error : colors.text, fontWeight: 700,
            }}>
              {current.type === NotificationType.WARNING ? '⚠ Warning' : 'Announcement'}
            </div>
            <div style={{ padding: spacing.lg }}>
              <div style={{ fontSize: fontSize.lg, fontWeight: 600, color: colors.text, marginBottom: spacing.sm }}>{current.title}</div>
              {current.body && <div className="notif-body" style={{ color: colors.text, fontSize: fontSize.sm }}
                onClick={e => followInAppLink(e, () => dismissModal(current))}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(current.body) }} />}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: spacing.lg }}>
                <button onClick={() => dismissModal(current)}
                  style={{ padding: '8px 20px', border: 'none', borderRadius: radius.sm, fontSize: fontSize.sm, fontWeight: 600, cursor: 'pointer',
                    background: current.type === NotificationType.WARNING ? colors.error : colors.primary, color: '#fff' }}>
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
